import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";

const STORAGE_KEY = "personaos.aiResearch.tracked";

type TrackedBrand = { id: string; name: string; startedAt: string };

function readTracked(): TrackedBrand[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter((x: any) => x && x.id) : [];
  } catch {
    return [];
  }
}

function writeTracked(list: TrackedBrand[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {}
}

export function trackAIResearch(id: string, name: string) {
  const list = readTracked();
  if (list.some((x) => x.id === id)) return;
  list.push({ id, name, startedAt: new Date().toISOString() });
  writeTracked(list);
  window.dispatchEvent(new Event("ai-research-tracker-update"));
}

export function untrackAIResearch(id: string) {
  const list = readTracked().filter((x) => x.id !== id);
  writeTracked(list);
  window.dispatchEvent(new Event("ai-research-tracker-update"));
}

export function AIResearchTracker() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const intervalRef = useRef<number | null>(null);
  const inFlightRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    let stopped = false;

    const poll = async () => {
      const list = readTracked();
      if (list.length === 0) return;

      // Auto-expire entries older than 20 min so a stuck job doesn't poll forever
      const now = Date.now();
      const fresh = list.filter((x) => now - new Date(x.startedAt).getTime() < 20 * 60 * 1000);
      if (fresh.length !== list.length) writeTracked(fresh);

      for (const item of fresh) {
        if (inFlightRef.current.has(item.id)) continue;
        inFlightRef.current.add(item.id);
        try {
          const res = await fetch(`/api/brands/${item.id}`, { credentials: "include" });
          if (!res.ok) continue;
          const brand: any = await res.json();
          const status = brand?.aiResearchStatus;
          if (status === "completed") {
            untrackAIResearch(item.id);
            queryClient.invalidateQueries({ queryKey: ["/api/brands", item.id] });
            toast({
              title: "AI Research complete",
              description: `${item.name || "Brand"} has been enriched. Open it to review.`,
            });
          } else if (status === "failed") {
            untrackAIResearch(item.id);
            queryClient.invalidateQueries({ queryKey: ["/api/brands", item.id] });
            toast({
              title: "AI Research failed",
              description: brand?.aiResearchError || `Could not enrich ${item.name || "brand"}.`,
              variant: "destructive",
            });
          }
        } catch {
          // network blip; try again on next tick
        } finally {
          inFlightRef.current.delete(item.id);
        }
      }
    };

    const start = () => {
      if (intervalRef.current != null) return;
      // Poll every 8s while there's at least one tracked job
      intervalRef.current = window.setInterval(() => {
        if (stopped) return;
        if (readTracked().length === 0) {
          if (intervalRef.current != null) {
            window.clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          return;
        }
        poll();
      }, 8000);
      poll();
    };

    const onUpdate = () => {
      if (readTracked().length > 0) start();
    };

    window.addEventListener("ai-research-tracker-update", onUpdate);
    if (readTracked().length > 0) start();

    return () => {
      stopped = true;
      window.removeEventListener("ai-research-tracker-update", onUpdate);
      if (intervalRef.current != null) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [toast, queryClient]);

  return null;
}
